<script lang="ts">
  /* Single full-width mic button — the entire UI of transcribe-web.
   * idle/error → click starts; recording → click stops.
   * Result is delivered to the embedding host via stop().then(getSnapshot()),
   * see docs/delphi-integration.md. No on-screen transcript by design. */
  import { recordingStore } from "../lib/recording.svelte";

  const rec = recordingStore();

  const isRecording = $derived(rec.state === "recording");
  const isBusy = $derived(rec.state === "requesting" || rec.state === "stopping" || rec.state === "sending");
  const isIdle = $derived(rec.state === "idle" || rec.state === "error");
  const isError = $derived(rec.state === "error");

  async function onClick(): Promise<void> {
    if (isIdle) {
      await rec.start();
    } else if (isRecording) {
      await rec.stop();
    }
  }

  function fmtElapsed(ms: number): string {
    const sec = Math.floor(ms / 1000);
    const mm = String(Math.floor(sec / 60)).padStart(2, "0");
    const ss = String(sec % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }
</script>

<button
  type="button"
  class="mic"
  class:recording={isRecording}
  class:busy={isBusy}
  class:error={isError}
  aria-pressed={isRecording}
  disabled={isBusy && !isRecording}
  onclick={onClick}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" x2="12" y1="19" y2="22" />
  </svg>
  {#if isRecording}
    <span class="timer" aria-live="polite">{fmtElapsed(rec.elapsedMs)}</span>
  {/if}
</button>

<style>
  .mic {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    color: var(--text);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background .15s, color .15s, border-color .15s;
    user-select: none;
    touch-action: none;
  }
  .mic:hover:not(:disabled):not(.recording):not(.busy):not(.error) {
    border-color: var(--primary);
    color: var(--primary);
  }
  .mic.recording {
    animation: rec-pulse 1.4s ease-in-out infinite;
  }
  .mic.busy {
    color: var(--muted);
    cursor: progress;
  }
  .mic.error {
    background: var(--danger);
    color: #fff;
    border-color: var(--danger);
  }
  .mic.error:hover { filter: brightness(1.05); }
  .mic:disabled { opacity: 0.45; cursor: not-allowed; }
  .mic .timer {
    font: 600 12px var(--font-mono);
    letter-spacing: 0.04em;
  }

  @keyframes rec-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(100, 116, 139, .35); }
    50%      { box-shadow: 0 0 0 8px rgba(100, 116, 139, 0); }
  }
</style>
